import React, { useCallback, useMemo, useState } from 'react';
import { Alert, Button, Checkbox, Drawer, Form, Input, Radio, Space, Typography, message } from 'antd';
import { CommentOutlined, SendOutlined } from '@ant-design/icons';
import { useTranslation } from '../../hooks/useTranslation';
import { useIsMobile } from '../../hooks/useIsMobile';
import { useFloatingAction } from '../FloatingActions';
import {
  clearUnsentFeedbackDraft,
  collectFeedbackDiagnostics,
  getUnsentFeedbackDraft,
  saveUnsentFeedbackDraft,
  submitUserFeedback,
  UserFeedbackPayload,
  UserFeedbackType,
} from '../../services/userFeedback';
import './index.css';

const { Text } = Typography;
const MAX_CONTENT_LENGTH = 2000;

interface FeedbackFormValues {
  feedbackType: UserFeedbackType;
  content: string;
  contact?: string;
  includeDiagnostics: boolean;
}

const UserFeedbackWidget: React.FC = () => {
  const { t, currentLanguage } = useTranslation();
  const [open, setOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [draft, setDraft] = useState(() => getUnsentFeedbackDraft());
  const [form] = Form.useForm<FeedbackFormValues>();
  const isMobile = useIsMobile(768);

  const typeOptions = useMemo(() => [
    { value: 'bug' as UserFeedbackType, label: t('userFeedback.types.bug') },
    { value: 'suggestion' as UserFeedbackType, label: t('userFeedback.types.suggestion') },
    { value: 'experience' as UserFeedbackType, label: t('userFeedback.types.experience') },
    { value: 'other' as UserFeedbackType, label: t('userFeedback.types.other') },
  ], [t]);

  const openDrawer = useCallback(() => {
    setOpen(true);
  }, []);

  const feedbackEntryLabel = t('userFeedback.entryTooltip');
  const feedbackAction = useMemo(() => ({
    key: 'user-feedback',
    order: 10,
    icon: <CommentOutlined />,
    label: feedbackEntryLabel,
    testId: 'user-feedback-entry',
    onClick: openDrawer,
  }), [feedbackEntryLabel, openDrawer]);

  useFloatingAction(feedbackAction);

  const restoreDraft = () => {
    if (!draft) return;
    form.setFieldsValue({
      feedbackType: draft.feedbackType,
      content: draft.content,
      contact: draft.contact,
      includeDiagnostics: Boolean(draft.diagnostics),
    });
    message.success(t('userFeedback.draftRestored'));
  };

  const clearDraft = () => {
    clearUnsentFeedbackDraft();
    setDraft(null);
    message.success(t('userFeedback.draftCleared'));
  };

  const buildPayload = (values: FeedbackFormValues): UserFeedbackPayload => {
    const diagnostics = values.includeDiagnostics
      ? collectFeedbackDiagnostics(currentLanguage)
      : undefined;

    return {
      feedbackType: values.feedbackType,
      content: values.content.trim(),
      contact: values.contact?.trim() || undefined,
      uid: diagnostics?.uid,
      diagnostics,
    };
  };

  const handleSubmit = async (values: FeedbackFormValues) => {
    const payload = buildPayload(values);
    setSubmitting(true);

    try {
      await submitUserFeedback(payload);
      clearUnsentFeedbackDraft();
      setDraft(null);
      form.resetFields();
      message.success(t('userFeedback.submitSuccess'));
      setOpen(false);
    } catch (error) {
      saveUnsentFeedbackDraft(payload);
      setDraft({ ...payload, savedAt: Date.now() });
      message.warning(t('userFeedback.submitFailedSaved'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Drawer
        title={t('userFeedback.drawerTitle')}
        placement={isMobile ? 'bottom' : 'right'}
        width={440}
        height="82vh"
        open={open}
        onClose={() => setOpen(false)}
        destroyOnClose={false}
        className="img-cloud-feedback-drawer"
      >
        <div className="img-cloud-feedback-intro">
          <Text type="secondary">{t('userFeedback.drawerSubtitle')}</Text>
        </div>

        {draft && (
          <Alert
            className="img-cloud-feedback-draft-alert"
            type="info"
            showIcon
            message={t('userFeedback.unsentDraftTitle')}
            description={t('userFeedback.unsentDraftDescription')}
            action={
              <Space size="small">
                <Button size="small" onClick={restoreDraft}>{t('userFeedback.restoreDraft')}</Button>
                <Button size="small" type="text" onClick={clearDraft}>{t('userFeedback.clearDraft')}</Button>
              </Space>
            }
          />
        )}

        <Form<FeedbackFormValues>
          form={form}
          layout="vertical"
          initialValues={{ feedbackType: 'bug', includeDiagnostics: true }}
          onFinish={handleSubmit}
          className="img-cloud-feedback-form"
        >
          <Form.Item
            name="feedbackType"
            label={t('userFeedback.typeLabel')}
            rules={[{ required: true, message: t('userFeedback.typeRequired') }]}
          >
            <Radio.Group className="img-cloud-feedback-type-group">
              {typeOptions.map((item) => (
                <Radio key={item.value} value={item.value}>
                  {item.label}
                </Radio>
              ))}
            </Radio.Group>
          </Form.Item>

          <Form.Item
            name="content"
            label={t('userFeedback.contentLabel')}
            rules={[
              { required: true, whitespace: true, message: t('userFeedback.contentRequired') },
              { max: MAX_CONTENT_LENGTH, message: t('userFeedback.contentTooLong') },
            ]}
          >
            <Input.TextArea
              autoSize={{ minRows: 6, maxRows: 10 }}
              maxLength={MAX_CONTENT_LENGTH}
              showCount
              placeholder={t('userFeedback.contentPlaceholder')}
            />
          </Form.Item>

          <Form.Item
            name="contact"
            label={t('userFeedback.contactLabel')}
            rules={[{ max: 255, message: t('userFeedback.contactTooLong') }]}
          >
            <Input placeholder={t('userFeedback.contactPlaceholder')} />
          </Form.Item>

          <Form.Item name="includeDiagnostics" valuePropName="checked" className="img-cloud-feedback-diagnostics-item">
            <Checkbox>{t('userFeedback.includeDiagnostics')}</Checkbox>
          </Form.Item>
          <Text className="img-cloud-feedback-diagnostics-help" type="secondary">
            {t('userFeedback.diagnosticsHelp')}
          </Text>

          <div className="img-cloud-feedback-actions">
            <Button onClick={() => setOpen(false)}>{t('common.cancel')}</Button>
            <Button type="primary" htmlType="submit" icon={<SendOutlined />} loading={submitting}>
              {t('userFeedback.submit')}
            </Button>
          </div>
        </Form>
      </Drawer>
    </>
  );
};

export default UserFeedbackWidget;
